import { Amplify } from 'aws-amplify';
import { confirmSignIn, fetchAuthSession, signIn, signOut, signUp } from 'aws-amplify/auth';
import axios from 'axios';
import awsConfig from '../../config/aws-config';
import { parsePhoneNumber } from '../../helper/phoneUtils';
import { USER_API_URL } from '../instance';

Amplify.configure(awsConfig);

// Read once at module load. Set REACT_APP_USER_TYPE=MOBILE in .env to test
// the mobile OTP bypass; leave unset or set to ADMIN for the real SNS flow.
const USER_TYPE = (process.env.REACT_APP_USER_TYPE as 'MOBILE' | 'ADMIN' | undefined) ?? 'ADMIN';

export const authService = {
  verifyUser: async (countryCode: string, phoneNumber: string) => {
    try {
      const response = await axios.post(
        `${USER_API_URL}/auth/admin/verify-user`,
        {
          countryCode,
          phoneNumber,
        },
        { timeout: 30000 },
      );
      return { success: true, data: response.data, isVerified: true };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'User verification failed');
    }
  },

  sendOTP: async (phoneNumber: string, recaptchaToken?: string) => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        return { success: true, message: 'Already signed in', alreadySignedIn: true };
      }

      // Verify reCAPTCHA token with backend if provided
      if (recaptchaToken) {
        try {
          const recaptchaResponse = await axios.post(
            `${USER_API_URL}/auth/verify-recaptcha`,
            { token: recaptchaToken },
            { timeout: 30000 },
          );

          if (!recaptchaResponse.data?.success) {
            throw new Error('reCAPTCHA verification failed. Please try again.');
          }
        } catch (recaptchaError: any) {
          console.error('reCAPTCHA verification error:', recaptchaError);
          throw new Error('reCAPTCHA verification failed. Please try again.');
        }
      }

      // Try to create user first (triggers Pre Sign-up Lambda)
      try {
        await signUp({
          username: phoneNumber,
          password: Math.random().toString(36).slice(-16) + 'Aa1!',
          options: {
            userAttributes: {
              phone_number: phoneNumber,
            },
          },
        });
      } catch (signUpError: any) {
        if (
          !signUpError.message?.includes('An account with the given phone_number already exists')
        ) {
          console.error('SignUp error (continuing):', signUpError.message);
        }
      }

      // Now initiate custom auth flow
      const result = await signIn({
        username: phoneNumber,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientMetadata: { userType: USER_TYPE },
        },
      });
      const challengeUsername =
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
          ? (result.nextStep as any).additionalInfo?.USERNAME
          : undefined;

      return {
        success: true,
        message: 'OTP sent successfully',
        challengeName: result.nextStep?.signInStep,
        username: challengeUsername,
      };
    } catch (error: any) {
      if (error.message?.includes('already signed in')) {
        return { success: true, message: 'Already signed in', alreadySignedIn: true };
      }
      throw new Error(error.message || 'Failed to send OTP');
    }
  },

  verifyOTP: async (phoneNumber: string, otp: string, usernameFromState: string) => {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: otp,
        options: { clientMetadata: { userType: USER_TYPE } },
      });

      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('Access Token Not Found');
      }

      const blueNumberResponse = await axios.get(`${USER_API_URL}/blue-number/${phoneNumber}`, {
        timeout: 30000,
      });
      const blueNumberData = blueNumberResponse.data;

      const { countryCode, phoneNumber: phoneOnly } = parsePhoneNumber(phoneNumber);
      const loginInfoResponse = await axios.post(
        `${USER_API_URL}/admin-user/update-logged-in-info`,
        {
          blueNumberId: blueNumberData?.blueNumber,
          phoneNumber: phoneOnly,
          countryCode: countryCode,
          username: usernameFromState,
        },
        { timeout: 30000 },
      );

      const roleInfo = loginInfoResponse.data?.data?.role;

      const userData = {
        accessToken,
        user: { phoneNumber, id: Date.now(), blueNumber: blueNumberData?.blueNumber },
      };

      localStorage.setItem('accessToken', accessToken || '');
      localStorage.setItem('userResponse', JSON.stringify(userData));
      localStorage.setItem('roleInfo', JSON.stringify(roleInfo));

      return { success: true, data: userData };
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      if (apiMessage)
        throw new Error(typeof apiMessage === 'string' ? apiMessage : JSON.stringify(apiMessage));
      throw new Error('Invalid OTP (or) Session Expired. Please check and try again.');
    }
  },

  resendOTP: async (phoneNumber: string) => {
    try {
      await signIn({
        username: phoneNumber,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientMetadata: { userType: USER_TYPE },
        },
      });
      return { success: true, message: 'OTP resent successfully' };
    } catch (error: any) {
      throw error;
    }
  },

  signOut: async () => {
    try {
      await signOut();
      localStorage.clear();
    } catch (error: any) {
      localStorage.clear();
    }
  },
};
